import React from 'react';
import {
  Phone,
  MessageSquare,
  MessagesSquare,
  Mail,
  CheckCircle2,
  Users,
  Play,
  ArrowRight,
  Brain,
  BookOpen,
  GitBranch,
  Database,
  BarChart3,
  Heart,
  Landmark,
  GraduationCap,
  Headphones,
  ShoppingBag,
  Diamond,
} from 'lucide-react';
import freshroadLogo from '../../../images/freshroadlogo.png';
import { PhoneGraphic, ChatGraphic, HeadsetGraphic, CalendarGraphic, ChartGraphic } from './solutionGraphics';
import { Breadcrumb, SectionBand, CtaBand, ProductLinks, PRODUCT_LOGIN } from './DemoSiteLayout';
import SampleCall from './SampleCall';
import { T, SURFACE, SHADOW, Hero, PrimaryButton, GhostButton, DemoLink, IconTile, CenterHead } from './siteUI';

/* =========================================================
   Conversational AI — product page.

   Merged from the former Customer AI Platform and Customer AI pages: this
   page keeps the platform structure and absorbs the five bespoke use-case
   illustrations that were the only unique content on the other one.
   ========================================================= */

/* ---------- hero diagram pieces ---------- */

const CHANNELS = [
  { name: 'Voice', icon: Phone, tint: 'bg-teal-50 text-teal-600' },
  { name: 'SMS', icon: MessageSquare, tint: 'bg-violet-50 text-violet-600' },
  { name: 'Chat', icon: MessagesSquare, tint: 'bg-sky-50 text-sky-600' },
  { name: 'Email', icon: Mail, tint: 'bg-orange-50 text-orange-500' },
];

const OUTCOMES = [
  { name: 'Completed Work', icon: CheckCircle2, tint: 'bg-emerald-50 text-emerald-600' },
  { name: 'Happy Customers', icon: Users, tint: 'bg-violet-50 text-violet-600' },
];

const ChipCard = ({ name, icon: Icon, tint }) => (
  <div className={`flex w-[92px] flex-col items-center gap-1.5 px-3 py-3 ${SURFACE.card}`}>
    <IconTile icon={Icon} tint={tint} size="sm" />
    <span className="text-center text-[11px] font-semibold leading-tight">{name}</span>
  </div>
);

const FEATURES = [
  { name: 'AI Receptionist', desc: 'Answer calls, qualify leads, and route to the right place.', graphic: PhoneGraphic, bg: 'bg-teal-50' },
  { name: 'Customer Service', desc: 'Resolve questions and issues across any channel.', graphic: ChatGraphic, bg: 'bg-emerald-50' },
  { name: 'Call Center', desc: 'Inbound and outbound calls at scale with AI agents.', graphic: HeadsetGraphic, bg: 'bg-violet-50' },
  { name: 'Appointment Scheduling', desc: 'Book, reschedule, and remind — automatically.', graphic: CalendarGraphic, bg: 'bg-amber-50' },
  { name: 'Research & Polling', desc: 'Surveys, polls, and market research powered by AI.', graphic: ChartGraphic, bg: 'bg-sky-50' },
];

/* ---------- sections data ---------- */

const HOW_STEPS = [
  { name: 'Understand Intent', icon: Brain },
  { name: 'Retrieve Knowledge', icon: BookOpen },
  { name: 'Execute Workflow', icon: GitBranch },
  { name: 'Integrate Systems', icon: Database },
  { name: 'Deliver Outcomes', icon: BarChart3 },
];

const OUTCOME_BULLETS = [
  'Voice, SMS, chat, and email in one platform',
  'Enterprise knowledge & RAG',
  'Workflow automation',
  'CRM, EHR, and 100+ integrations',
  'Analytics & conversation insights',
];

const TRADITIONAL = ['Human agents', 'Training & management', 'Tools & licenses', 'Overtime & turnover'];
const FRESHROAD = ['AI agents 24/7', 'No overtime', 'Lower cost per resolution', 'Scale instantly'];

const INDUSTRIES = [
  { name: 'Healthcare', desc: 'Improve access and reduce admin burden', icon: Heart, tint: 'text-violet-600 bg-violet-50' },
  { name: 'Financial Services', desc: 'Resolve inquiries and increase conversions', icon: Landmark, tint: 'text-emerald-600 bg-emerald-50' },
  { name: 'Education', desc: 'Support students and staff around the clock', icon: GraduationCap, tint: 'text-sky-600 bg-sky-50' },
  { name: 'Call Center', desc: 'Handle more calls, reduce wait times, and increase first-call resolution', icon: Headphones, tint: 'text-teal-600 bg-teal-50' },
  { name: 'Retail & eCommerce', desc: 'Deliver 24/7 support and boost customer loyalty', icon: ShoppingBag, tint: 'text-orange-500 bg-orange-50' },
];

const Sparkline = () => {
  const pts = [4, 9, 7, 11, 9, 14, 13, 12, 16, 15, 20, 24];
  const max = 26;
  const path = pts
    .map((v, i) => `${i === 0 ? 'M' : 'L'}${(i / (pts.length - 1)) * 260},${60 - (v / max) * 55}`)
    .join(' ');
  return (
    <svg viewBox="0 0 260 64" className="mt-4 w-full">
      <path d={path} fill="none" stroke="#0D9488" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map((v, i) => (
        <circle key={i} cx={(i / (pts.length - 1)) * 260} cy={60 - (v / max) * 55} r="2.5" fill="#0D9488" />
      ))}
    </svg>
  );
};

/* ---------- page ---------- */

const DemoSiteConversationalAI = () => (
  <>
    <Breadcrumb trail={['Home', 'Products', 'Conversational AI']} />

    {/* Hero */}
    <section className="grid grid-cols-1 items-center gap-12 py-8 lg:grid-cols-2">
      {/* No eyebrow: the breadcrumb above already places the page, and an
          eyebrow here would just repeat the h1. Home uses an eyebrow instead
          because it has no breadcrumb. */}
      <Hero
        title="Conversational AI"
        deck={
          <>
            AI agents that turn conversations into <span className="text-teal-600">completed work.</span>
          </>
        }
        actions={
          <>
            <DemoLink />
            <ProductLinks login={PRODUCT_LOGIN.conversational} pricing="/site/pricing" />
          </>
        }
        footerLead={
          <SampleCall
            src="/audio/conversational-ai.mp3"
            label="Hear a real call"
            caption="Inbound enquiry, answered and resolved end to end"
          />
        }
        footer={
          <>
            <p className="max-w-[30ch] text-[19px] font-bold leading-[1.28] tracking-[-0.016em]">
              Every conversation,{' '}
              <span className="text-teal-600">completed.</span>
            </p>
            <p className={`mt-2.5 max-w-[52ch] ${T.body}`}>
              Voice, SMS, chat and email in one agent — answering, resolving and completing the work
              behind every conversation.
            </p>
          </>
        }
      >
        Engage across voice, SMS, chat, and email. Resolve issues, book appointments, qualify leads,
        and more — 24/7.
      </Hero>

      {/* diagram (fills the column; scales with the page) */}
      <div className="flex w-full items-center gap-2 overflow-x-auto pb-1">
        <div className="flex shrink-0 flex-col gap-3">
          {CHANNELS.map((c) => (
            <ChipCard key={c.name} {...c} />
          ))}
        </div>
        <div className="h-px min-w-[16px] flex-1 border-t-2 border-dashed border-slate-300" />
        {/* Same signature hub as the home hero — the one element carrying depth. */}
        <div
          className="relative flex h-[170px] w-[170px] shrink-0 flex-col items-center justify-center gap-2 rounded-full bg-white ring-1 ring-slate-900/5 shadow-[0_0_0_12px_rgba(20,184,166,0.07),0_0_0_26px_rgba(20,184,166,0.035),0_2px_4px_rgba(15,23,42,0.05),0_16px_40px_-16px_rgba(15,23,42,0.2)]"
        >
          <img src={freshroadLogo} alt="" className="h-10 w-10 object-contain" />
          <p className="text-center text-[13px] font-bold leading-tight">
            FreshRoad
            <br />
            <span className="font-medium text-slate-500">Conversational AI</span>
          </p>
        </div>
        <div className="h-px min-w-[16px] flex-1 border-t-2 border-dashed border-slate-300" />
        <div className="flex shrink-0 flex-col gap-4">
          {OUTCOMES.map((o) => (
            <ChipCard key={o.name} {...o} />
          ))}
        </div>
      </div>
    </section>

    {/* The five things it actually handles — illustrations carried over from
        the retired Customer AI page, which is where they earn their card. */}
    <section className="px-2 py-8">
      <CenterHead
        title="Every customer conversation, handled."
        subtitle="Five ways teams put Conversational AI to work across voice, SMS, chat and email."
      />
      <div className="mt-9 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
        {FEATURES.map(({ name, desc, graphic: Graphic, bg }) => (
          <div key={name} className={`flex flex-col p-4 ${SURFACE.cardHover}`}>
            <div className={`flex h-36 items-center justify-center overflow-hidden rounded-xl ${bg}`}>
              <Graphic />
            </div>
            <p className="mt-4 text-[15px] font-semibold leading-snug">{name}</p>
            <p className={`mt-1.5 flex-1 ${T.micro}`}>{desc}</p>
          </div>
        ))}
      </div>
    </section>

    {/* How it works — no icons. The top of this page already carries the
        channel diagram and five illustrated cards; another row of tiles made
        it read as icon soup. The step number is the only marker needed. */}
    <SectionBand tinted title="How it works">
      <ol className="mx-auto mt-8 grid max-w-[900px] grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-5">
        {HOW_STEPS.map(({ name }, i) => (
          <li key={name} className="flex items-start gap-3 lg:flex-col lg:items-start lg:gap-2.5">
            <span className="text-[13px] font-bold text-teal-600 tabular-nums">
              {String(i + 1).padStart(2, '0')}
            </span>
            <span className="text-[13.5px] font-semibold leading-snug">{name}</span>
          </li>
        ))}
      </ol>
    </SectionBand>

    {/* Outcomes */}
    <section
      className={`grid grid-cols-1 items-center gap-10 px-8 py-10 md:px-12 lg:grid-cols-2 ${SURFACE.band}`}
    >
      <div>
        <h2 className={T.h2}>Built for real business outcomes</h2>
        <ul className="mt-6 space-y-3.5">
          {OUTCOME_BULLETS.map((b) => (
            <li key={b} className="flex items-start gap-3 text-[14px] text-slate-700">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-teal-50 text-teal-600">
                <ArrowRight size={12} strokeWidth={2.6} />
              </span>
              {b}
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-2xl bg-slate-50/60 p-6 ring-1 ring-slate-900/5">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="text-[12px] text-slate-500">Conversations</p>
            <p className="mt-1 text-[30px] font-bold tracking-[-0.02em]">1,248</p>
            <p className="mt-1 text-[12px] font-semibold text-emerald-600">↑ 89%</p>
          </div>
          <div>
            <p className="text-[12px] text-slate-500">Resolution rate</p>
            <p className="mt-1 text-[30px] font-bold tracking-[-0.02em]">92%</p>
            <p className="mt-1 text-[12px] font-semibold text-emerald-600">↑ 14%</p>
          </div>
        </div>
        <Sparkline />
      </div>
    </section>

    {/* Cost savings */}
    <SectionBand
      tinted
      title="Massive cost savings"
      subtitle="Automate conversations. Resolve more. Save up to 94% compared to traditional operations."
    >
      <div className="mx-auto mt-8 grid max-w-[720px] grid-cols-1 gap-5 md:grid-cols-2">
        <div className="rounded-2xl bg-slate-50/60 p-6 ring-1 ring-slate-900/5">
          <p className="text-[13px] font-semibold text-slate-500">Traditional operations</p>
          <p className="mt-2 text-[32px] font-bold tracking-[-0.02em] text-slate-400">$150,000</p>
          <p className={T.micro}>For 10,000 conversations</p>
          <ul className="mt-4 space-y-2.5">
            {TRADITIONAL.map((t) => (
              <li key={t} className="flex items-center gap-2.5 text-[13px] text-slate-500">
                <Diamond size={12} className="shrink-0 text-slate-300" /> {t}
              </li>
            ))}
          </ul>
        </div>
        {/* The winning column is the one card that earns emphasis in this band. */}
        <div className={`rounded-2xl bg-teal-50/50 p-6 ring-2 ring-teal-500/70 ${SHADOW.raised}`}>
          <p className="text-[13px] font-semibold text-teal-700">FreshRoad Customer AI</p>
          <p className="mt-2 text-[32px] font-bold tracking-[-0.02em] text-teal-700">$9,000</p>
          <p className={T.micro}>For 10,000 conversations</p>
          <ul className="mt-4 space-y-2.5">
            {FRESHROAD.map((t) => (
              <li key={t} className="flex items-center gap-2.5 text-[13px] text-slate-600">
                <Diamond size={12} className="shrink-0 text-teal-500" /> {t}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="mt-6 text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-teal-600 px-5 py-2 text-[13px] font-bold text-white">
          94% cost reduction
        </span>
      </div>
    </SectionBand>

    {/* Industry impact */}
    <SectionBand tinted title="Industry impact">
      <div className="mt-8 grid grid-cols-2 gap-8 md:grid-cols-5">
        {INDUSTRIES.map(({ name, desc, icon: Icon, tint }) => (
          <div key={name} className="flex flex-col items-center text-center">
            <IconTile icon={Icon} tint={tint} />
            <p className="mt-3 text-[13.5px] font-semibold">{name}</p>
            <p className={`mt-1.5 ${T.micro}`}>{desc}</p>
          </div>
        ))}
      </div>
    </SectionBand>

    <CtaBand
      title="Ready to transform customer conversations?"
      subtitle="Book a demo and see the platform in action."
      primary="Book a Demo"
    />
  </>
);

export default DemoSiteConversationalAI;
